import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useId, useState } from 'react';
import { formatCashMinor } from './cashMoney';
const copy = {
    custody: ['Деньги на руках', 'Əldə olan pul', 'Money in custody'],
    unavailable: ['остаток недоступен', 'qalıq əlçatan deyil', 'balance unavailable'],
    partial: ['известная часть', 'məlum hissə', 'known part'],
    incomplete: ['итог неполный', 'yekun tam deyil', 'total incomplete'],
    draft: ['Начальный остаток не задан', 'İlkin qalıq daxil edilməyib', 'Opening balance not set'],
    store: ['Филиал', 'Filial', 'Branch'],
    selected: ['Выбрано', 'Seçilib', 'Selected'],
    collapse: ['Свернуть филиал', 'Filialı yığ', 'Collapse branch'],
    expand: ['Раскрыть филиал', 'Filialı aç', 'Expand branch'],
    search: ['Поиск', 'Axtar', 'Search'],
};
export function cashAccountGroupText(locale, key) {
    return copy[key][locale === 'az' ? 1 : locale === 'en' ? 2 : 0];
}
/** Caller filters scope actions before adapting them. This never adds grants,
 * inferred status, balances, or accounts outside those exact account scopes. */
export function cashScopedAccounts(scopes, accounts) {
    const result = new Map();
    for (const scope of scopes) {
        if (!scope.accountId)
            continue;
        const existing = accounts.find(account => account.id === scope.accountId && account.storeId === scope.storeId);
        if (scope.accountStatus === 'ARCHIVED' || existing?.status === 'ARCHIVED')
            continue;
        const status = scope.accountStatus ?? existing?.status;
        const kind = scope.accountKind ?? existing?.kind;
        const currency = scope.accountCurrency ?? existing?.currency;
        if (!status || !kind || !currency || kind === 'TRANSIT')
            continue;
        result.set(scope.accountId, {
            id: scope.accountId,
            storeId: scope.storeId,
            storeName: scope.storeName ?? existing?.storeName,
            name: scope.accountName ?? existing?.name ?? scope.accountId,
            status,
            kind,
            currency,
            // A visible financial row may carry a balance; scope metadata never does.
            ...(existing?.balanceMinor !== undefined ? { balanceMinor: existing.balanceMinor } : {}),
        });
    }
    return [...result.values()];
}
const knownBalance = (value) => value !== undefined && /^-?\d+$/.test(value);
const workingKind = (kind) => kind !== undefined && ['CASH', 'BANK', 'OTHER'].includes(kind);
function totals(accounts) {
    const currencies = [...new Set(accounts.map(account => account.currency))].sort();
    return currencies.map(currency => {
        const rows = accounts.filter(account => account.currency === currency);
        const known = rows.filter(account => knownBalance(account.balanceMinor));
        return {
            currency,
            ...(known.length
                ? { knownMinor: known.reduce((sum, row) => sum + BigInt(row.balanceMinor), 0n).toString() }
                : {}),
            coverage: known.length === rows.length ? 'FULL' : known.length ? 'PARTIAL' : 'NONE',
        };
    });
}
/** Current working balances within the caller's already authorized, eligible scope. */
export function cashAccountTotals(accounts) {
    return totals(accounts.filter(account => account.status === 'ACTIVE' && workingKind(account.kind)));
}
/** Totals describe only this supplied collection, never the entire branch. */
export function groupCashAccounts(accounts, stores, locale) {
    const byStore = new Map();
    for (const account of accounts) {
        if (account.status === 'ARCHIVED' || account.kind === 'TRANSIT')
            continue;
        const rows = byStore.get(account.storeId) || [];
        rows.push(account);
        byStore.set(account.storeId, rows);
    }
    const compare = (left, right) => left.name.localeCompare(right.name, locale) || left.id.localeCompare(right.id);
    return [...byStore]
        .map(([storeId, rows]) => {
        const active = rows.filter(account => account.status !== 'DRAFT');
        return {
            storeId,
            storeName: stores.find(store => store.id === storeId)?.name ||
                rows.find(row => row.storeName)?.storeName ||
                cashAccountGroupText(locale, 'store'),
            accounts: [...rows].sort(compare),
            workingTotals: totals(active.filter(account => account.status === 'ACTIVE' && workingKind(account.kind))),
            custodyTotals: totals(active.filter(account => account.status === 'ACTIVE' && account.kind === 'CUSTODY')),
            projectedTotals: totals(active
                .filter(account => account.status === undefined ||
                (!workingKind(account.kind) && account.kind !== 'CUSTODY'))
                .map(account => ({ ...account, balanceMinor: undefined }))),
            draftCount: rows.filter(account => account.status === 'DRAFT').length,
        };
    })
        .sort((left, right) => compare({ name: left.storeName, id: left.storeId }, { name: right.storeName, id: right.storeId }));
}
export function cashAccountGroupTotalLabel(total, locale) {
    if (total.knownMinor === undefined)
        return `${total.currency}: ${cashAccountGroupText(locale, 'unavailable')}`;
    const amount = formatCashMinor(total.knownMinor, total.currency, locale);
    return total.coverage === 'FULL'
        ? amount
        : `${cashAccountGroupText(locale, 'partial')}: ${amount} (${cashAccountGroupText(locale, 'incomplete')})`;
}
export function cashAccountGroupSummary(group, locale) {
    const labels = group.workingTotals.map(total => cashAccountGroupTotalLabel(total, locale));
    if (group.custodyTotals.length)
        labels.push(`${cashAccountGroupText(locale, 'custody')}: ${group.custodyTotals.map(total => cashAccountGroupTotalLabel(total, locale)).join('; ')}`);
    labels.push(...group.projectedTotals.map(total => cashAccountGroupTotalLabel(total, locale)));
    return labels.join(' · ');
}
export function CashAccountGroupSummary({ group, locale, }) {
    return (_jsxs("div", { className: "cash-account-group-summary", children: [_jsx("strong", { children: group.storeName }), _jsx("small", { children: cashAccountGroupSummary(group, locale) })] }));
}
export function cashAccountOptionLabel(account, storeName, locale) {
    const balance = account.status === 'DRAFT'
        ? cashAccountGroupText(locale, 'draft')
        : knownBalance(account.balanceMinor)
            ? formatCashMinor(account.balanceMinor, account.currency, locale)
            : `${account.currency}: ${cashAccountGroupText(locale, 'unavailable')}`;
    return `${account.name} · ${storeName} · ${balance}${account.kind === 'CUSTODY' ? ` · ${cashAccountGroupText(locale, 'custody')}` : ''}`;
}
/** Render directly inside a native select. The caller owns selection and eligibility. */
export function CashAccountOptions({ accounts, stores, locale, }) {
    return (_jsx(_Fragment, { children: groupCashAccounts(accounts, stores, locale).map(group => (_jsx("optgroup", { label: `${group.storeName} · ${cashAccountGroupSummary(group, locale)}`, children: group.accounts.map(account => (_jsx("option", { value: account.id, children: cashAccountOptionLabel(account, group.storeName, locale) }, account.id))) }, group.storeId))) }));
}
/** Groups an existing list without owning its account actions or navigation. */
export function CashAccountGroups({ accounts, stores, locale, renderAccount, }) {
    return _jsx(_Fragment, { children: groupCashAccounts(accounts, stores, locale).map(group => (_jsx(CashAccountBranchDisclosure, { group: group, locale: locale, children: group.accounts.map(account => _jsx("div", { children: renderAccount(account) }, account.id)) }, group.storeId))) });
}
/** Hidden panels keep account forms mounted; disclosure changes presentation only. */
export function CashAccountBranchDisclosure({ group, locale, children, selectedIds, role }) {
    const [expanded, setExpanded] = useState(true);
    const id = useId();
    const selected = group.accounts.filter(account => selectedIds?.includes(account.id));
    return _jsxs("section", { className: "cash-account-group", role: role, "aria-label": group.storeName, children: [_jsxs("button", { type: "button", className: "cash-account-group-toggle", "aria-expanded": expanded, "aria-controls": id, "aria-describedby": `${id}-summary${selectedIds ? ' ' + id + '-selected' : ''}`, "aria-label": `${cashAccountGroupText(locale, expanded ? 'collapse' : 'expand')}: ${group.storeName}`, onClick: () => setExpanded(value => !value), children: [_jsxs("span", { className: "cash-account-group-summary", children: [_jsx("strong", { children: group.storeName }), _jsx("small", { id: `${id}-summary`, children: cashAccountGroupSummary(group, locale) }), selectedIds && _jsxs("small", { id: `${id}-selected`, children: [cashAccountGroupText(locale, 'selected'), ": ", selected.length, selected.length > 0 ? ` · ${selected.map(account => account.name).join(' · ')}` : ''] })] }), _jsx("span", { className: "cash-account-group-chevron", "aria-hidden": "true", children: _jsx("svg", { width: "18", height: "18", viewBox: "0 0 20 20", fill: "none", stroke: "currentColor", strokeWidth: "1.75", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: expanded ? 'm5 12 5-5 5 5' : 'm5 8 5 5 5-5' }) }) })] }), _jsx("div", { id: id, hidden: !expanded, children: children })] });
}
/** One fieldset and one radio name across branches preserve keyboard selection. */
export function CashAccountChoiceGroup({ accounts, stores, locale, label, value, onChange, disabled, help, action, leadingOptions = [], selectOnly = false, selectionContext, searchDescriptions = false, searchLabel, }) {
    const id = useId();
    const [search, setSearch] = useState('');
    const allGroups = groupCashAccounts(accounts, stores, locale);
    const allAccounts = allGroups.flatMap(group => group.accounts);
    const needsSearch = searchDescriptions ||
        (!selectOnly &&
            (allAccounts.length + leadingOptions.length > 6 ||
                allAccounts.some(account => account.name.length > 35)));
    const query = needsSearch ? search.trim().toLocaleLowerCase(locale) : '';
    const matches = (text) => !query || text.toLocaleLowerCase(locale).includes(query);
    const filteredAccounts = allAccounts.filter(account => account.id === value ||
        matches([
            account.name,
            stores.find(store => store.id === account.storeId)?.name ?? account.storeName,
            account.currency,
        ].join(' ')));
    const groups = groupCashAccounts(filteredAccounts, stores, locale);
    const available = [
        ...leadingOptions.filter(option => !option.disabled).map(option => option.id),
        ...allAccounts.map(account => account.id),
    ];
    const count = leadingOptions.length + allAccounts.length;
    useEffect(() => {
        if (!disabled && !value && available.length === 1 && available[0] !== '')
            onChange(available[0]);
    }, [disabled, value, available.join('\u0000'), onChange]);
    if (count === 1 && available.length === 1 && (!value || value === available[0])) {
        const account = allAccounts[0];
        const group = allGroups[0];
        const option = leadingOptions[0];
        return (_jsxs("div", { className: "cash-field", children: [_jsx("span", { children: label }), group && _jsx(CashAccountGroupSummary, { group: group, locale: locale }), _jsx("strong", { children: account?.name ?? option?.label }), account && group ? (_jsxs("small", { children: [group.storeName, " \u00B7 ", account.currency, account.status === 'DRAFT' ? ` · ${cashAccountGroupText(locale, 'draft')}` : ''] })) : (option?.description && _jsx("small", { children: option.description })), selectionContext && _jsx("small", { children: selectionContext }), help && _jsx("small", { children: help }), action] }));
    }
    if (selectOnly || count > 6 || needsSearch)
        return (_jsxs("div", { className: "cash-field", children: [_jsx("label", { htmlFor: id, children: label }), needsSearch && (_jsx("input", { type: "search", "aria-label": `${label}: ${searchLabel ?? cashAccountGroupText(locale, 'search')}`, value: search, disabled: disabled, onChange: event => setSearch(event.target.value) })), _jsxs("div", { className: "cash-account-choice", children: [_jsxs("select", { id: id, value: value, disabled: disabled, onChange: event => onChange(event.target.value), "aria-describedby": help ? `${id}-help` : undefined, children: [!leadingOptions.some(option => option.id === '') && _jsx("option", { value: "", children: "\u2014" }), leadingOptions
                                    .filter(option => option.id === value ||
                                    matches([option.label, searchDescriptions ? option.description : ''].join(' ')))
                                    .map(option => (_jsxs("option", { value: option.id, disabled: option.disabled, children: [option.label, option.description ? ` · ${option.description}` : ''] }, option.id))), _jsx(CashAccountOptions, { accounts: filteredAccounts, stores: stores, locale: locale })] }), selectionContext && _jsx("small", { children: selectionContext })] }), help && _jsx("small", { id: `${id}-help`, children: help }), action] }));
    const radio = (option) => (_jsxs("label", { className: "cash-choice", children: [_jsx("input", { type: "radio", name: id, value: option.id, checked: value === option.id, disabled: option.disabled, onChange: () => onChange(option.id) }), _jsxs("span", { className: "cash-choice-content", children: [_jsx("span", { className: "cash-choice-mark", "aria-hidden": "true", children: value === option.id ? '✓' : '' }), _jsxs("span", { children: [_jsx("strong", { children: option.label }), option.description && _jsx("small", { children: option.description })] })] })] }, option.id));
    return (_jsxs("fieldset", { className: "cash-choice-group", disabled: disabled, role: "radiogroup", "aria-labelledby": `${id}-label`, "aria-describedby": help ? `${id}-help` : undefined, children: [_jsx("legend", { id: `${id}-label`, children: label }), action && _jsx("div", { className: "cash-choice-action", children: action }), leadingOptions.length > 0 && (_jsx("div", { className: "cash-choice-grid", children: leadingOptions.map(radio) })), groups.map(group => (_jsx(CashAccountBranchDisclosure, { role: "group", group: group, locale: locale, selectedIds: [value], children: _jsx("div", { className: "cash-choice-grid", children: group.accounts.map(account => radio({
                        id: account.id,
                        label: account.name,
                        description: cashAccountOptionLabel(account, group.storeName, locale),
                    })) }) }, group.storeId))), help && (_jsx("p", { className: "cash-choice-help", id: `${id}-help`, children: help }))] }));
}
